/**
 * Enhanced Device Fingerprinting Utility
 * Generates comprehensive device identifiers and detects incognito/private browsing mode
 * Used for device trust management and progressive security with incognito detection
 */

/**
 * Device Fingerprinting Data Interface
 */
export interface DeviceFingerprintData {
  // Basic device info
  fingerprint: string;
  userAgent: string;
  platform: string;
  language: string;
  timezone: string;
  screenResolution: string;
  colorDepth: number;
  pixelRatio: number;
  deviceMemory?: string;
  hardwareConcurrency?: number;
  cookiesEnabled: boolean;
  doNotTrack: string | null;

  // Incognito detection results
  incognitoDetected: boolean;
  localStorageAvailable: boolean;
  sessionStorageAvailable: boolean;
  indexedDBAvailable: boolean;

  // Canvas fingerprinting
  canvasFingerprint: string;

  // Metadata
  timestamp: string;
  fingerprintVersion: string;
}

/**
 * Generate a unique device fingerprint
 * Uses crypto.randomUUID() for secure, collision-resistant identifiers
 * Stores fingerprint in localStorage for persistence across sessions
 */
export const generateDeviceFingerprint = (): string => {
  try {
    // Generate a cryptographically secure UUID
    const fingerprint = crypto.randomUUID();

    // Store in localStorage for persistence
    localStorage.setItem('amalq_device_fingerprint', fingerprint);

    return fingerprint;
  } catch (error) {
    console.error('🔐 Device Fingerprint: Failed to generate UUID:', error);
    // Fallback to timestamp-based ID if crypto.randomUUID fails
    const fallbackId = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('amalq_device_fingerprint', fallbackId);
    return fallbackId;
  }
};

/**
 * Get the current device fingerprint
 * Returns existing fingerprint or generates a new one if none exists
 */
export const getDeviceFingerprint = (): string => {
  try {
    // Try to get existing fingerprint from localStorage
    const existingFingerprint = localStorage.getItem('amalq_device_fingerprint');

    if (existingFingerprint && existingFingerprint.trim() !== '') {
      return existingFingerprint;
    }

    // Generate new fingerprint if none exists
    return generateDeviceFingerprint();
  } catch (error) {
    console.error('🔐 Device Fingerprint: Failed to get fingerprint from localStorage:', error);
    // Fallback to generation if localStorage fails
    return generateDeviceFingerprint();
  }
};

/**
 * Reset device fingerprint (useful for testing or security)
 * Generates a new fingerprint and replaces the old one
 */
export const resetDeviceFingerprint = (): string => {
  return generateDeviceFingerprint();
};


/**
 * Test localStorage availability and quota
 * Returns true if localStorage works normally, false if incognito mode
 */
export const testLocalStorage = (): boolean => {
  try {
    const testKey = '__incognito_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Test sessionStorage availability
 * Returns true if sessionStorage works normally, false if incognito mode
 */
export const testSessionStorage = (): boolean => {
  try {
    const testKey = '__session_test__';
    sessionStorage.setItem(testKey, '1');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Test IndexedDB availability
 * Returns true if IndexedDB is available, false if incognito mode
 */
export const testIndexedDB = (): boolean => {
  return !!window.indexedDB;
};

/**
 * Generate canvas fingerprint for additional device uniqueness
 * Canvas fingerprinting creates a unique signature based on graphics rendering
 */
export const generateCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return 'no_canvas';
    }

    // Set canvas size
    canvas.width = 200;
    canvas.height = 50;

    // Draw background
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text with specific styling
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('AmalQ Device Fingerprint', 2, 2);

    // Draw some shapes
    ctx.fillStyle = 'rgba(0, 102, 204, 0.5)';
    ctx.fillRect(125, 1, 62, 20);

    // Draw a circle
    ctx.beginPath();
    ctx.arc(180, 25, 10, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(204, 0, 102, 0.6)';
    ctx.fill();

    // Get data URL (this will be different across devices/browsers)
    const dataURL = canvas.toDataURL();

    // Create a hash-like string from the canvas data
    let hash = 0;
    for (let i = 0; i < dataURL.length; i++) {
      const char = dataURL.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  } catch (e) {
    return 'canvas_error';
  }
};

/**
 * Detect incognito/private browsing mode
 * Uses confidence-based scoring for reliable detection
 * Based on browser storage and API behavior differences
 */
export const detectIncognitoMode = (): boolean => {
  let confidenceScore = 0;

  // Test 1: localStorage quota (most reliable indicator)
  // Incognito mode typically has much lower storage limits
  if (testLocalStorageQuota()) {
    confidenceScore += 40; // High weight for storage quota failure
  }

  // Test 2: IndexedDB availability
  // IndexedDB may be restricted in incognito mode
  if (!testIndexedDBAvailability()) {
    confidenceScore += 25; // Significant indicator
  }

  // Test 3: WebKit File System (Safari-specific)
  if (testWebkitRequestFileSystem()) {
    confidenceScore += 15; // Safari-specific indicator
  }

  // Test 4: Canvas fingerprinting behavior
  if (testCanvasFingerprinting()) {
    confidenceScore += 10; // Minor indicator
  }

  // Test 5: Navigator storage estimate (modern browsers)
  if (testNavigatorStorageQuota()) {
    confidenceScore += 10; // Additional confirmation
  }

  const isIncognito = confidenceScore >= 50; // Require 50%+ confidence

  return isIncognito;
};

/**
 * Test localStorage quota limitations (common in incognito mode)
 * Uses smaller test size to avoid false positives in normal browsers
 */
const testLocalStorageQuota = (): boolean => {
  try {
    const testKey = '__quota_test__';
    // Use 100KB instead of 1MB to avoid false positives
    // Incognito mode typically has much lower limits (< 100KB)
    const testData = 'x'.repeat(100 * 1024);

    localStorage.setItem(testKey, testData);
    localStorage.removeItem(testKey);
    return false; // Normal mode - storage worked
  } catch (e) {
    return true; // Incognito mode - quota exceeded
  }
};

/**
 * Test IndexedDB availability (often disabled in incognito)
 */
const testIndexedDBAvailability = (): boolean => {
  try {
    // Simple synchronous check for IndexedDB availability
    // In incognito mode, IndexedDB may be disabled or restricted
    if (!window.indexedDB) {
      return false; // Not available
    }

    // Check if the basic methods exist
    const idb = window.indexedDB;
    return typeof idb.open === 'function' && typeof idb.deleteDatabase === 'function';
  } catch (e) {
    return false; // Error accessing IndexedDB
  }
};

/**
 * Test WebKit File System API (often disabled in incognito)
 */
const testWebkitRequestFileSystem = (): boolean => {
  // This API is mainly available in WebKit browsers
  const webkitWindow = window as unknown as Record<string, unknown>;
  if (!webkitWindow.webkitRequestFileSystem) {
    return false; // Not applicable
  }

  try {
    const requestFileSystem = webkitWindow.webkitRequestFileSystem as (
      type: number,
      size: number,
      successCallback: () => void,
      errorCallback: () => void
    ) => void;
    const TEMPORARY = webkitWindow.TEMPORARY as number;

    requestFileSystem(
      TEMPORARY,
      1024 * 1024, // 1MB
      () => {}, // Success
      () => {} // Error (incognito might cause this)
    );
    return false; // Normal mode
  } catch (e) {
    return true; // Incognito mode
  }
};

/**
 * Test Navigator Storage API quota (modern browsers)
 * Incognito mode may have different storage limits
 */
const testNavigatorStorageQuota = (): boolean => {
  if (!navigator.storage || !navigator.storage.estimate) {
    return false; // Not applicable
  }

  try {
    // This is asynchronous, but we'll do a basic check
    // In incognito mode, quota might be much lower
    const estimate = navigator.storage.estimate();
    return estimate && typeof estimate.then === 'function'; // API available
  } catch (e) {
    return false; // Error accessing storage estimate
  }
};

/**
 * Test canvas fingerprinting differences (incognito may behave differently)
 */
const testCanvasFingerprinting = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return true; // Incognito mode (canvas disabled)
    }

    // Draw something and check if it renders
    ctx.fillText('test', 0, 10);
    const data = ctx.getImageData(0, 0, 1, 1).data;

    // If all pixels are transparent/black, might be incognito
    return data[0] === 0 && data[1] === 0 && data[2] === 0 && data[3] === 0;
  } catch (e) {
    return true; // Incognito mode
  }
};

/**
 * Generate comprehensive device fingerprint data
 * Includes incognito detection and enhanced security features
 */
export const generateDeviceFingerprintData = (): DeviceFingerprintData => {
  const fingerprint = getDeviceFingerprint();

  // Validate that we have a proper UUID
  if (!fingerprint || typeof fingerprint !== 'string' || fingerprint.trim() === '') {
    console.error('🔐 Device Fingerprint: Invalid fingerprint generated:', fingerprint);
    throw new Error('Failed to generate valid device fingerprint');
  }

  // Basic device information
  const basicInfo = {
    fingerprint,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    deviceMemory: (navigator as unknown as Record<string, unknown>).deviceMemory as string || 'unknown',
    hardwareConcurrency: navigator.hardwareConcurrency || undefined,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
  };

  // Incognito detection
  const incognitoResults = {
    incognitoDetected: detectIncognitoMode(),
    localStorageAvailable: testLocalStorage(),
    sessionStorageAvailable: testSessionStorage(),
    indexedDBAvailable: testIndexedDB(),
  };

  // Canvas fingerprinting
  const canvasFingerprint = generateCanvasFingerprint();

  // Metadata
  const metadata = {
    timestamp: new Date().toISOString(),
    fingerprintVersion: '2.0.0', // Version for backend compatibility
  };

  const fingerprintData: DeviceFingerprintData = {
    ...basicInfo,
    ...incognitoResults,
    canvasFingerprint,
    ...metadata,
  };

  return fingerprintData;
};

/**
 * Check if device fingerprinting is supported
 * Returns true if crypto.randomUUID is available and localStorage works
 */
export const isDeviceFingerprintingSupported = (): boolean => {
  try {
    // Check if crypto.randomUUID is available
    if (!crypto || !crypto.randomUUID) {
      return false;
    }

    // Check if localStorage is available
    if (!localStorage) {
      return false;
    }

    // Test localStorage functionality
    const testKey = 'amalq_fingerprint_test';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);

    return true;
  } catch (error) {
    console.error('🔐 Device Fingerprint: Support check failed:', error);
    return false;
  }
};

/**
 * Initialize device fingerprinting on app startup
 * Should be called early in the app lifecycle
 */
export const initializeDeviceFingerprinting = (): void => {
  if (isDeviceFingerprintingSupported()) {
    // Ensure we have a fingerprint
    getDeviceFingerprint();
  }
};

/**
 * Get comprehensive device information for progressive UX
 * Returns device details for smart login indicators and security dashboard
 */
export const getDeviceInfo = (): Record<string, unknown> => {
  try {
    return {
      fingerprint: getDeviceFingerprint(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      deviceMemory: (navigator as unknown as Record<string, unknown>).deviceMemory as string || 'unknown',
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    // Silently fallback to minimal device info if collection fails
    return {
      fingerprint: getDeviceFingerprint(),
      userAgent: 'unknown',
      platform: 'unknown',
      language: 'unknown',
      timezone: 'unknown',
      screenResolution: 'unknown',
      colorDepth: 0,
      deviceMemory: 'unknown',
      hardwareConcurrency: 'unknown',
      cookiesEnabled: false,
      doNotTrack: null,
      timestamp: new Date().toISOString(),
    };
  }
};
